import { normalizeStatusName } from '../subgroup-helpers/subgroup-helpers';
import type { MutableStatusLabelMap, StatusLabelMap, StatusLabelResolver } from './status-labels.type';

/**
 * Records a label under its normalized key, resolving conflicts so the result
 * does not depend on the order boards are merged in.
 *
 * Two rules, applied in order:
 * 1. A label equal to its own normalized key (e.g. `"in progress"`) carries no
 *    casing information, so any cased label (`"In Progress"`) beats it. The
 *    reverse never happens. This is the backfill-window case: one board has
 *    been converted to source casing and another has not.
 * 2. When two boards both report cased-but-different labels (`"In Progress"`
 *    vs `"IN PROGRESS"`, two Jira sites naming the same status differently),
 *    the lexicographically smaller one wins. Arbitrary but deterministic —
 *    without it the label displayed would flip depending on which board's
 *    response landed first, which changes between page loads.
 *
 * @param {MutableStatusLabelMap} map - The map to mutate
 * @param {string} normalizedKey - Normalized status key to store under
 * @param {string} label - Candidate display label in whatever casing the API returned
 * @returns {void}
 */
const setPreferredLabel = (map: MutableStatusLabelMap, normalizedKey: string, label: string): void => {
  const existing = map.get(normalizedKey);
  if (existing === undefined) {
    map.set(normalizedKey, label);
    return;
  }

  const existingCarriesCasing = existing !== normalizedKey;
  const candidateCarriesCasing = label !== normalizedKey;

  if (!existingCarriesCasing && candidateCarriesCasing) {
    map.set(normalizedKey, label);
    return;
  }
  if (existingCarriesCasing && candidateCarriesCasing && label < existing) {
    map.set(normalizedKey, label);
  }
};

/**
 * Builds a normalized-key lookup from one board's status names.
 *
 * Blank entries are dropped, and same-status duplicates that differ only by
 * casing collapse to the most informative variant.
 *
 * @param {readonly string[] | undefined} statuses - Status names as returned for a single board
 * @returns {StatusLabelMap} Lookup from normalized key to source-cased label
 */
const buildStatusLabelMap = (statuses: readonly string[] | undefined): StatusLabelMap => {
  const map: MutableStatusLabelMap = new Map();
  if (!statuses) {
    return map;
  }
  statuses.forEach((status) => {
    if (typeof status !== 'string') {
      return;
    }
    const normalizedKey = normalizeStatusName(status);
    if (!normalizedKey) {
      return;
    }
    setPreferredLabel(map, normalizedKey, status);
  });
  return map;
};

/**
 * Merges several boards' label maps into one lookup.
 *
 * Needed because most status-rendering surfaces span more than one board — a
 * process-map comparison view shows two, and an initiative can contribute
 * dozens. Conflicts resolve via the same rules `buildStatusLabelMap` uses, so
 * the merged result is identical regardless of the order boards resolve in —
 * which matters because the per-board fetches land in a nondeterministic
 * order.
 *
 * @param {StatusLabelMap[]} maps - Per-board label maps to combine
 * @returns {StatusLabelMap} A single lookup covering every board
 */
const mergeStatusLabelMaps = (maps: StatusLabelMap[]): StatusLabelMap => {
  const merged: MutableStatusLabelMap = new Map();
  maps.forEach((map) => {
    map.forEach((label, normalizedKey) => {
      setPreferredLabel(merged, normalizedKey, label);
    });
  });
  return merged;
};

/**
 * Resolves a raw status value to its display label.
 *
 * Returns the input verbatim when the status is unknown to the map, which is
 * the intended behavior for Jira Data Center boards, boards awaiting backfill,
 * and statuses no longer present on a board. Deliberately does NOT title-case
 * the fallback: seeing the raw lowercase value is how a missing or failed
 * status fetch stays visible instead of being cosmetically papered over.
 *
 * @param {string} rawStatus - A status or section value as stored/returned by the API
 * @param {StatusLabelMap} labelMap - Lookup built from the statuses endpoint
 * @returns {string} The source-cased label, or `rawStatus` unchanged
 */
const resolveStatusLabel = (rawStatus: string, labelMap: StatusLabelMap): string => {
  if (!rawStatus) {
    return rawStatus;
  }
  return labelMap.get(normalizeStatusName(rawStatus)) ?? rawStatus;
};

/**
 * Wraps a label map into a standalone resolver.
 *
 * Lets plain helpers that render status text (chart metadata builders, filter
 * option builders, event-label formatters) accept a single function parameter
 * instead of importing the map type and the resolve call.
 *
 * @param {StatusLabelMap} labelMap - Lookup built from the statuses endpoint
 * @returns {StatusLabelResolver} A resolver closed over that map
 */
const createStatusLabelResolver = (labelMap: StatusLabelMap): StatusLabelResolver => {
  return (rawStatus: string) => resolveStatusLabel(rawStatus, labelMap);
};

/**
 * Resolver that returns every status unchanged.
 *
 * Used as the fallback when a consumer renders outside a `StatusLabelsProvider`
 * so it degrades to the pre-existing raw-value behavior rather than crashing.
 */
const identityStatusLabelResolver: StatusLabelResolver = (rawStatus: string) => rawStatus;

export {
  buildStatusLabelMap,
  createStatusLabelResolver,
  identityStatusLabelResolver,
  mergeStatusLabelMaps,
  resolveStatusLabel,
  setPreferredLabel,
};
